/**
 * AI Language Helper
 * 
 * Zentraler Helper für die Sprachsteuerung aller KI-Services.
 * Stellt sicher, dass KI-Antworten in der korrekten Sprache generiert werden.
 */

import i18n from '../i18n';

// Supported languages
export type SupportedLanguage = 'de' | 'en';

export interface AILanguageConfig {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  instruction: string;
  promptSuffix: string;
}

/**
 * Get the current system language configuration for AI services
 */
export const getAILanguage = (): AILanguageConfig => {
  const lang = (i18n.language?.substring(0, 2) || 'de') as SupportedLanguage;
  
  if (lang === 'de') {
    return {
      code: 'de',
      name: 'German',
      nativeName: 'Deutsch',
      instruction: 'Antworte auf Deutsch.',
      promptSuffix: 'WICHTIG: Alle Antworten müssen auf Deutsch sein. Verwende keine englischen Begriffe.'
    };
  }
  
  return {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    instruction: 'Respond in English.',
    promptSuffix: 'IMPORTANT: All responses must be in English.'
  };
};

/**
 * Get language configuration for a specific language code
 */
export const getLanguageConfig = (langCode?: string): AILanguageConfig => {
  const lang = (langCode?.substring(0, 2) || i18n.language?.substring(0, 2) || 'de') as SupportedLanguage;
  
  if (lang === 'de') {
    return {
      code: 'de',
      name: 'German',
      nativeName: 'Deutsch',
      instruction: 'Antworte auf Deutsch.',
      promptSuffix: 'WICHTIG: Alle Antworten müssen auf Deutsch sein. Verwende keine englischen Begriffe.'
    };
  }
  
  return {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    instruction: 'Respond in English.',
    promptSuffix: 'IMPORTANT: All responses must be in English.'
  };
};

/**
 * Get a language prompt suffix for AI requests
 * Can be used with either system language or a specific override
 */
export const getLanguagePrompt = (langCode?: string): string => {
  const config = getLanguageConfig(langCode);
  return config.promptSuffix;
};

/**
 * Get short language instruction for inline use
 */
export const getLanguageInstruction = (langCode?: string): string => {
  const config = getLanguageConfig(langCode);
  return config.instruction;
};

/**
 * Create a system prompt with language instruction
 */
export const createSystemPromptWithLanguage = (basePrompt: string, langCode?: string): string => {
  const config = getLanguageConfig(langCode);
  return `${basePrompt}\n\n${config.promptSuffix}`;
};

/**
 * Detect language from website metadata or content
 * Used during onboarding to determine the language of scraped content
 */
export const detectLanguageFromMetadata = (metadata: { language?: string; lang?: string }): SupportedLanguage => {
  const lang = metadata?.language || metadata?.lang || '';
  const langCode = lang.substring(0, 2).toLowerCase();
  
  if (langCode === 'de') return 'de';
  if (langCode === 'en') return 'en';
  
  // Default to German for DACH region
  return 'de';
};

/**
 * Get greeting in the current language
 */
export const getGreeting = (langCode?: string): string => {
  const config = getLanguageConfig(langCode);
  return config.code === 'de' ? 'Hallo!' : 'Hello!';
};

/**
 * Get common AI assistant phrases in the current language
 */
export const getAIPhrases = (langCode?: string) => {
  const config = getLanguageConfig(langCode);
  
  if (config.code === 'de') {
    return {
      greeting: 'Hallo! 👋',
      howCanIHelp: 'Wie kann ich dir helfen?',
      thinking: 'Ich denke nach...',
      analyzing: 'Ich analysiere...',
      done: 'Fertig!',
      error: 'Es ist ein Fehler aufgetreten.',
      noResults: 'Keine Ergebnisse gefunden.',
      suggestions: 'Hier sind meine Vorschläge:',
      tip: 'Tipp',
      recommendation: 'Empfehlung',
      warning: 'Warnung',
      success: 'Erfolg',
    };
  }
  
  return {
    greeting: 'Hello! 👋',
    howCanIHelp: 'How can I help you?',
    thinking: 'Thinking...',
    analyzing: 'Analyzing...',
    done: 'Done!',
    error: 'An error occurred.',
    noResults: 'No results found.',
    suggestions: 'Here are my suggestions:',
    tip: 'Tip',
    recommendation: 'Recommendation',
    warning: 'Warning',
    success: 'Success',
  };
};

/**
 * Format a prompt for multilingual AI responses
 * Ensures the AI responds in the correct language
 */
export const formatPromptForLanguage = (
  prompt: string, 
  langCode?: string,
  context?: string
): string => {
  const config = getLanguageConfig(langCode);
  
  const languageBlock = config.code === 'de' 
    ? `
🌐 SPRACHE: Deutsch
- Antworte ausschließlich auf Deutsch
- Verwende keine englischen Fachbegriffe, wenn deutsche existieren
- Halte den Ton professionell aber freundlich
`
    : `
🌐 LANGUAGE: English
- Respond exclusively in English
- Keep the tone professional but friendly
`;

  return `${languageBlock}

${context ? `CONTEXT: ${context}\n\n` : ''}${prompt}`;
};

export default {
  getAILanguage,
  getLanguageConfig,
  getLanguagePrompt,
  getLanguageInstruction,
  createSystemPromptWithLanguage,
  detectLanguageFromMetadata,
  getGreeting,
  getAIPhrases,
  formatPromptForLanguage,
};

